import { useQuery, useMutation } from "@tanstack/react-query";
import api from "services/api";
import {
  Device,
  DevicesResponse,
  DeviceStatusResponse,
} from "types/device.types";
import {
  DeviceLinkResponse,
  DeviceHistoryResponse,
  DeviceNotificationsResponse,
  FirmwareUpdateRequest,
  FirmwareUpdateResponse,
  ModeChangeRequest,
  ModeChangeResponse,
  ScheduleUpdateRequest,
  ScheduleUpdateResponse,
  DeviceTargetsUpdateRequest,
  DeviceTargetsUpdateResponse,
  DeviceRegionUpdateRequest,
  DeviceRegionUpdateResponse,
  DevicePoolSizeUpdateRequest,
  DevicePoolSizeUpdateResponse,
  UpdateNotificationReadResponse,
} from "types/deviceHooks.types";

/**
 * Hook to fetch device list with different endpoints based on user role
 * @param page Page number
 * @param limit Items per page limit
 * @param sortField Field for sorting
 * @param sortOrder Sort direction
 * @param search Search term
 * @param isAdminView If true, fetches all devices (admin view), if false, fetches only dealer devices
 * @param latitude Latitude for location-based search
 * @param longitude Longitude for location-based search
 * @param radiusKm Radius in kilometers for location-based search
 */
export const useDevices = (
  page = 1,
  limit = 20,
  sortField?: string,
  sortOrder?: string,
  search?: string,
  isAdminView = false,
  latitude?: number,
  longitude?: number,
  radiusKm?: number,
) => {
  return useQuery({
    queryKey: [
      "devices",
      page,
      limit,
      sortField,
      sortOrder,
      search,
      isAdminView,
      latitude,
      longitude,
      radiusKm,
    ],
    refetchOnWindowFocus: true,
    staleTime: 0,
    queryFn: async (): Promise<DevicesResponse> => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (sortField) {
        params.append("sortField", sortField);
      }

      if (sortOrder) {
        params.append("sortOrder", sortOrder);
      }

      if (search && search.trim() !== "") {
        params.append("search", search);
      }

      // Add location parameters if provided
      if (latitude !== undefined && longitude !== undefined) {
        params.append("latitude", latitude.toString());
        params.append("longitude", longitude.toString());
      }

      if (radiusKm !== undefined) {
        params.append("radiusKm", radiusKm.toString());
      }

      // Use appropriate endpoint based on user role
      const endpoint = isAdminView ? "admin/devices/all" : "dpp/dealer/devices";

      const response = await api.get<DevicesResponse>(
        `${endpoint}?${params.toString()}`,
      );
      return response.data;
    },
    select: (data) => ({
      success: data.success,
      response: data.response,
      pagination: data.pagination,
    }),
  });
};

export const useDevice = (
  serialNumber: string | number,
  linkId?: string | number | null,
) => {
  return useQuery({
    queryKey: ["getDevice", serialNumber, linkId ?? null],
    refetchOnWindowFocus: true,
    staleTime: 0,
    queryFn: async (): Promise<Device> => {
      const params = new URLSearchParams({
        serialNumber: String(serialNumber),
      });
      if (
        linkId !== undefined &&
        linkId !== null &&
        linkId !== "" &&
        String(linkId) !== "undefined" &&
        String(linkId) !== "null"
      ) {
        params.set("linkId", String(linkId));
      }
      const { data } = await api.get<any>(
        `dpp/device/withlocation?${params.toString()}`,
      );
      return data.data;
    },
    enabled: !!serialNumber,
  });
};

/**
 * Hook to fetch status of a specific device by ID
 */
export const useDeviceStatus = (serialNumber: string | number) => {
  return useQuery({
    queryKey: ["deviceStatus", serialNumber],
    refetchOnWindowFocus: true,
    refetchInterval: 15000,

    staleTime: 0, // 5 minutes
    queryFn: async (): Promise<DeviceStatusResponse> => {
      const response = await api.get<DeviceStatusResponse>(
        `dpp/device/status/${serialNumber}`,
      );
      return response.data;
    },
    enabled: !!serialNumber,
  });
};

/**
 * Hook to create a link between dealer and device
 */
export const useCreateDealerDeviceLink = () => {
  return useMutation({
    mutationFn: async ({
      serialNumber,
      clientAuthorizerEmail,
      skippedBle = false,
    }: {
      serialNumber: string;
      clientAuthorizerEmail: string;
      skippedBle?: boolean;
    }): Promise<DeviceLinkResponse> => {
      const response = await api.post<DeviceLinkResponse>(
        "dpp/dealer/devices/link",
        {
          serialNumber,
          email: clientAuthorizerEmail,
          skippedBle,
        },
      );
      return response.data;
    },
    onError: (error) => {
      console.error("Error creating device link:", error);
    },
  });
};

/**
 * Hook to remove link between dealer and device
 */
export const useRemoveDeviceLink = () => {
  return useMutation({
    mutationFn: async (linkId: string): Promise<{ success: boolean }> => {
      const response = await api.delete(`dpp/dealer/devices/link/${linkId}`);
      return response.data;
    },
    onError: (error) => {
      console.error("Error removing device link:", error);
    },
  });
};

/**
 * Hook to fetch history of a specific device by serialNumber
 */
export const useDeviceHistory = (serialNumber: string | number) => {
  return useQuery({
    queryKey: ["deviceHistory", serialNumber],
    queryFn: async (): Promise<DeviceHistoryResponse> => {
      const { data } = await api.get<DeviceHistoryResponse>(
        `dpp/device-history/${serialNumber}`,
      );

      return data;
    },
    enabled: !!serialNumber,
  });
};

/**
 * Hook to fetch notifications/alarms of a specific device by serialNumber
 */
export const useDeviceAlarms = (serialNumber: string | number) => {
  return useQuery({
    queryKey: ["deviceAlarms", serialNumber],
    queryFn: async (): Promise<DeviceNotificationsResponse> => {
      const response = await api.get<DeviceNotificationsResponse>(
        `dpp/device/notifications/${serialNumber}`,
      );

      return response.data;
    },
    enabled: !!serialNumber,
  });
};

/**
 * Hook to update device firmware
 */
export const useFirmwareUpdate = () => {
  return useMutation({
    mutationFn: async (
      updateData: FirmwareUpdateRequest,
    ): Promise<FirmwareUpdateResponse> => {
      const response = await api.post("dpp/device/update-firmware", updateData);
      return response.data;
    },
  });
};

/**
 * Hook to update device modes
 */
export const useUpdateDeviceModes = () => {
  return useMutation({
    mutationFn: async (
      updateData: ModeChangeRequest,
    ): Promise<ModeChangeResponse> => {
      const response = await api.post<ModeChangeResponse>(
        "dpp/device/update-modes",
        updateData,
      );
      return response.data;
    },
    onError: (error) => {
      console.error("Error updating device modes:", error);
    },
    onSuccess: (data) => {
      console.log("Device modes updated successfully:", data);
    },
  });
};

/**
 * Hook to update device schedules
 */
export const useUpdateSchedules = () => {
  return useMutation({
    mutationFn: async (
      updateData: ScheduleUpdateRequest,
    ): Promise<ScheduleUpdateResponse> => {
      const response = await api.post<ScheduleUpdateResponse>(
        "dpp/device/update-schedules",
        updateData,
      );
      return response.data;
    },
    onSuccess: (response, variables) => {
      console.log("Schedules updated successfully:", response);
    },
    onError: (error) => {
      console.error("Error updating device schedules:", error);
    },
  });
};

/**
 * Hook to update device targets/parameters
 */
export const useUpdateDeviceParams = () => {
  return useMutation({
    mutationFn: async (
      updateData: DeviceTargetsUpdateRequest,
    ): Promise<DeviceTargetsUpdateResponse> => {
      const response = await api.post<DeviceTargetsUpdateResponse>(
        "dpp/device/change-targets",
        updateData,
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Device parameters updated successfully:", data);
    },
    onError: (error) => {
      console.error("Error updating device parameters:", error);
    },
  });
};

/**
 * Hook to update device region (admin only)
 */
export const useUpdateDeviceRegion = () => {
  return useMutation({
    mutationFn: async (
      updateData: DeviceRegionUpdateRequest,
    ): Promise<DeviceRegionUpdateResponse> => {
      const response = await api.post<DeviceRegionUpdateResponse>(
        "dpp/device/config/region",
        updateData,
      );
      return response.data;
    },
  });
};

/**
 * Hook to update device pool size (admin only)
 */
export const useUpdateDevicePoolSize = () => {
  return useMutation({
    mutationFn: async (
      updateData: DevicePoolSizeUpdateRequest,
    ): Promise<DevicePoolSizeUpdateResponse> => {
      const response = await api.post<DevicePoolSizeUpdateResponse>(
        "dpp/device/config/pool-size",
        updateData,
      );
      return response.data;
    },
  });
};

/**
 * Hook to update device location
 */
export const useUpdateDeviceLocation = () => {
  return useMutation({
    mutationFn: async ({
      serialNumber,
      coordinates,
      address,
    }: {
      serialNumber: string;
      coordinates: {
        lat: number;
        lng: number;
      };
      address: string;
    }) => {
      const response = await api.post(`dpp/device-location`, {
        serialNumber,
        coordinates,
        address,
      });
      return response.data;
    },
    onError: (error) => {
      console.error("Error updating device location:", error);
    },
    onSuccess: (data) => {
      console.log("Device location updated successfully:", data);
    },
  });
};

/**
 * Hook to fetch users connected to a specific device
 */
export const useDeviceUsers = (deviceId: string) => {
  return useQuery({
    queryKey: ["deviceUsers", deviceId],
    queryFn: async () => {
      const response = await api.get(
        `dpp/device/${deviceId}/users`,
        // `http://localhost:3000/lgportal-uat/device/${deviceId}/users`
      );
      // Return the users array directly from the response data structure
      return response.data.users || [];
    },
    enabled: !!deviceId,
  });
};

/**
 * Hook to remove a user from a device
 */
export const useRemoveUserFromDevice = () => {
  return useMutation({
    mutationFn: async ({
      deviceId,
      userId,
    }: {
      deviceId: number;
      userId: number;
    }) => {
      const response = await api.post(
        `admin/device/user/revoke`,
        // `http://localhost:3000/lgportal-uat/user/device/revoke`,
        {
          userId,
          deviceId,
        },
      );
      return response.data;
    },
  });
};

/**
 * Hook to add a user to a device
 */
export const useAddUserToDevice = () => {
  return useMutation({
    mutationFn: async ({
      email,
      deviceId,
    }: {
      deviceId: number;
      email: string;
    }) => {
      const response = await api.post(
        `admin/device/user/assign`,
        // `http://localhost:3000/lgportal-uat/user/device/assign`,
        { email, deviceId },
      );
      return response.data;
    },
  });
};

/**
 * Hook to mark a notification as read
 */
export const useUpdateNotificationRead = () => {
  return useMutation({
    mutationFn: async (
      notificationId: number,
    ): Promise<UpdateNotificationReadResponse> => {
      const response = await api.put<UpdateNotificationReadResponse>(
        `dpp/device/notification/${notificationId}`,
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Notification marked as read successfully:", data);
    },
    onError: (error) => {
      console.error("Error marking notification as read:", error);
    },
  });
};

/**
 * Hook to update device link alias
 */
export const useUpdateDeviceLinkAlias = () => {
  return useMutation({
    mutationFn: async ({
      linkId,
      linkAlias,
    }: {
      linkId: number;
      linkAlias: string;
    }) => {
      const response = await api.put(
        `dpp/dealer/devices/link/${linkId}/alias`,
        {
          linkAlias,
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log("Device link alias updated successfully:", data);
    },
    onError: (error) => {
      console.error("Error updating device link alias:", error);
    },
  });
};

// Export types
;
